import { useCallback, useEffect, useState } from "react";
import { Lock, Users } from "lucide-react";
import { api } from "@/lib/api";
import { mxn } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { ROL_LABEL, sumaMontos } from "@/lib/integrantesGrupo";
import { IntegrantesCaptura } from "@/components/solicitudes/IntegrantesCaptura";
import { IntegrantesContratacion } from "@/components/solicitudes/IntegrantesContratacion";

function aviso(info) {
  if (info.cancelada) return "Solicitud cancelada: los integrantes se conservan como evidencia.";
  if (info.contratado) return "Crédito grupal contratado con estos integrantes.";
  if (!info.integrantes.length && info.contratable)
    return "Autorizada antes de capturar integrantes en la solicitud: se capturan al contratar en Originación, con la suma igual al monto autorizado.";
  if (!info.integrantes.length) return "Sin integrantes capturados.";
  if (info.contratable) return "Autorizada por el Comité: se contrata en Originación, donde solo se ajustan los montos.";
  if (info.sellada) return "Enviada al Comité: los integrantes quedaron fijos y el Comité los revisa.";
  return "Solo lectura.";
}

function IntegrantesLectura({ info }) {
  return (
    <div data-testid="integrantes-lectura">
      <ul className="space-y-1">
        {info.integrantes.map((i) => (
          <li key={i.cliente_id} data-testid={`integrante-lectura-${i.cliente_id}`}
            className="flex flex-wrap items-center gap-2 rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-[13px]">
            <Lock className="h-3.5 w-3.5 text-slate-400" />
            <span className="font-medium text-slate-800">{i.cliente || i.cliente_id}</span>
            <span className="text-slate-500">· {ROL_LABEL[i.rol] || i.rol}</span>
            <span className="ml-auto font-semibold text-slate-700">{mxn(i.monto)}</span>
          </li>
        ))}
      </ul>
      <p className="mt-2 text-[11px] text-slate-500" data-testid="integrantes-aviso">{aviso(info)}</p>
    </div>
  );
}

/** Crédito grupal: integrantes de la solicitud (captura → Comité → contratación). */
export function IntegrantesGrupoPanel({ solicitudId, onGuardado, onContratar, busy, puedeContratar }) {
  const { can } = useAuth();
  const [info, setInfo] = useState(null);
  const load = useCallback(() => api.get(`/solicitudes/${solicitudId}/integrantes`)
    .then((r) => setInfo(r.data)).catch(() => setInfo(null)), [solicitudId]);
  useEffect(() => { load(); }, [load]);
  if (!info?.es_grupal) return null;

  const modo = info.editable && can("solicitudes", "editar") ? "captura"
    : info.contratable && onContratar ? (info.integrantes.length ? "contratar" : "contratar-libre") : "lectura";
  return (
    <div className="mt-5 rounded-lg border border-emerald-200 bg-emerald-50/40 p-3" data-testid="grupal-panel">
      <p className="mb-1 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-emerald-700">
        <Users className="h-4 w-4" /> Integrantes del grupo · obligados solidarios del total
        <span className="ml-auto normal-case text-slate-600" data-testid="grupal-total">{info.integrantes.length} · {mxn(sumaMontos(info.integrantes))}</span>
      </p>
      <p className="mb-3 text-[11px] text-slate-500">
        Se capturan en la solicitud, antes de enviarla al Comité. Mínimo 2, exactamente 1 titular, sin clientes repetidos y monto mayor a $0. La suma es el monto solicitado del grupo.
      </p>
      {modo === "captura" && <IntegrantesCaptura key={info.integrantes.length} info={info} onGuardado={() => { load(); onGuardado?.(); }} />}
      {modo === "contratar" && <IntegrantesContratacion info={info} busy={busy} puedeContratar={puedeContratar} onContratar={onContratar} />}
      {modo === "contratar-libre" && (
        <>
          <p className="mb-2 text-[11px] text-amber-700" data-testid="integrantes-captura-al-contratar">
            Esta solicitud se autorizó antes de capturar integrantes en la solicitud: captúralos aquí. La suma debe ser igual al monto autorizado por el Comité.
          </p>
          <IntegrantesCaptura info={info} contratar={{ onContratar, busy, puedeContratar }} />
        </>
      )}
      {modo === "lectura" && <IntegrantesLectura info={info} />}
    </div>
  );
}
